import {useCallback, useMemo} from 'react'
import {useDispatch} from 'react-redux'
import {AppState} from '../state'
import {ApplicationModal} from './actions'
import {useAppSelector, useAppDispatch} from '../hooks'
import {addPopup, PopupContent, setOpenModal, removePopup, updateTermsOfServiceStatus, UserTermsOfServiceStatus} from './actions'
import {useMultichainContext} from '../multichain/useMultichainContext'

export function useBlockNumber(): number | undefined {
  const {chainId: contextChainId} = useMultichainContext()
  return useAppSelector((state: AppState) => state.application.blockNumber[contextChainId ?? -1])
}

// returns a function that allows adding a popup
export function useAddPopup(): (content: PopupContent, key?: string) => void {
  const dispatch = useDispatch()

  return useCallback(
    (content: PopupContent, key?: string) => {
      dispatch(addPopup({content, key}))
    },
    [dispatch],
  )
}

// returns a function that allows removing a popup via its key
export function useRemovePopup(): (key: string) => void {
  const dispatch = useDispatch()
  return useCallback(
    (key: string) => {
      dispatch(removePopup({key}))
    },
    [dispatch],
  )
}

// get the list of active popups
export function useActivePopups(): AppState['application']['popupList'] {
  const list = useAppSelector((state: AppState) => state.application.popupList)
  return useMemo(() => list.filter(item => item.show), [list])
}

export function useModalOpen(modal: ApplicationModal): boolean {
  const openModal = useAppSelector((state: AppState) => state.application.openModal)
  return openModal === modal
}

export function useToggleModal(modal: ApplicationModal): () => void {
  const open = useModalOpen(modal)
  const dispatch = useAppDispatch()
  return useCallback(() => dispatch(setOpenModal(open ? null : modal)), [dispatch, modal, open])
}

export function useWalletModalToggle(): () => void {
  return useToggleModal(ApplicationModal.WALLET)
}

export function useTermsOfServiceModalToggle(): () => void {
  return useToggleModal(ApplicationModal.TERMS_OF_SERVICE)
}

export function useSetSlippageModalToggle(): () => void {
  return useToggleModal(ApplicationModal.SLIPPAGE)
}

export function useTermsOfServiceStatus(): UserTermsOfServiceStatus {
  return useAppSelector(state => state.application.termsOfServiceStatus)
}

export function useTermsOfServiceStatusManager(): [UserTermsOfServiceStatus, (termsOfServiceStatus: UserTermsOfServiceStatus) => void] {
  const dispatch = useAppDispatch()
  const status = useTermsOfServiceStatus()

  const setStatus = useCallback(
    (termsOfServiceStatus: UserTermsOfServiceStatus) => {
      dispatch(updateTermsOfServiceStatus({termsOfServiceStatus}))
    },
    [dispatch],
  )

  return [status, setStatus]
}

export function useApplicationState(): AppState['application'] {
  return useAppSelector(state => state.application)
}
